import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { v4 as uuidv4 } from 'uuid';
import { DataImport, DataImportKindEnum } from 'graphql-common';
import Modal from '@lib/components/Modal/Modal';
import Trans from '@lib/components/Trans/Trans';
import { IconName } from '@lib/components/Modal/enums';
import Typography from '@lib/components/Typography/Typography';
import processErrors from './utils/processErrors';
import styles from './ImportErrorsModal.module.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dataImport: DataImport | null | undefined;
  config: object;
  activeImportId?: string;
}

function ImportErrorsModal({
  isOpen,
  onClose,
  dataImport,
  config,
  activeImportId,
}: Props) {
  const { t } = useTranslation();

  const {
    kind,
    failureDetails,
    fileException,
    id: importId,
  } = useMemo(
    () => ({
      kind: _get(dataImport, 'kind') as DataImportKindEnum,
      failureDetails: _get(dataImport, 'failureDetails'),
      fileException: _get(dataImport, 'fileException', [] as string[]),
      id: _get(dataImport, 'id'),
    }),
    [dataImport],
  );

  const { processedErrors, errorsTotalCount } = useMemo(
    () =>
      processErrors({
        failureDetails,
        config,
        t,
        kind,
      }),
    [failureDetails, config, t, kind],
  );

  const description = useMemo(
    () => (
      <>
        {activeImportId === importId && (
          <>
            <Trans i18nKey="correct-errors-and-try" />
            <br />
          </>
        )}
        {fileException?.length ? (
          fileException.map((error) => <div key={uuidv4()}>{error}</div>)
        ) : (
          <Trans
            i18nKey="cells-with-errors"
            values={{ count: errorsTotalCount }}
          />
        )}
      </>
    ),
    [activeImportId, importId, fileException, errorsTotalCount],
  );

  return (
    <Modal
      description={description}
      icon={IconName.Warning}
      isOpen={isOpen}
      onClose={onClose}
      title={t('import-error')}
      modalContentClassName={styles.modalContentClassName}
    >
      <div className={styles.content}>
        {processedErrors.map((item) => (
          <div key={uuidv4()}>
            <Typography variant="body" strong className={styles.title}>
              {item.fieldName}
            </Typography>
            {item.errors.map((error) => (
              <Typography
                key={uuidv4()}
                variant="body"
                strong
                className={styles.text}
              >
                {error}
              </Typography>
            ))}
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default ImportErrorsModal;
